/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import api, { formatNumber, removeUndefinedAndNull } from "@/src/utils";
import { useQuery } from "@tanstack/react-query";
import { OfferInfo, OffersData } from "../types";
import { textVariants } from "@/src/components/Text/Text";
import { Avatar } from "@mantine/core";
import { Button } from "@/src/components/Button/Button";
import { cn } from "@/lib/utils";
import { Modal } from "@/src/components/Modal/Modal";
import { useState } from "react";
import PreviewOffer from "./PreviewOffer";
import { useNavigate } from "react-router-dom";
import Loader from "@/src/components/Loader";
import useUrlState from "@/src/hooks/use-url-state";
import useDebounce from "@/src/hooks/useDebounce";
import { ChevronLeft, ChevronRight } from "lucide-react";
import _, { isEmpty } from "lodash";
import EmptyPage from "@/src/components/EmptyPage/EmptyPage";
import { useTranslation } from "react-i18next";
import { TOptions } from "i18next";
import { parseAsArrayOf, parseAsInteger, useQueryState } from "nuqs";

const Offers = () => {
  const { t } = useTranslation();
  const trans = (key: string, options?: TOptions) =>
    t(`app.p2p.ads.${key}`, options);
  const lang = (key: string, options?: TOptions) =>
    t(`app.components.${key}`, options);
  const [view, setView] = useState(false);
  const [singleOffer, setSingleOffer] = useState<OfferInfo | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const [page, setPage] = useUrlState("page", 1);
  const [coin] = useQueryState("coin", {
    parse: Number,
  });
  const [offerType] = useQueryState("offerType", {
    defaultValue: "sell",
  });
  const [currency] = useQueryState("currency");
  const [tradingAmount] = useQueryState("tradingAmount", {});
  const [paymentMethods] = useQueryState(
    "paymentMethods",
    parseAsArrayOf(parseAsInteger)
  );
  const debAmount = useDebounce(tradingAmount, 1500);

  const fetchOffers = async () => {
    const res = await api.post(apiRoutes.offers, {
      type: "fetch",

      filters: removeUndefinedAndNull({
        coin__id: coin ?? undefined,
        offer_type: offerType,
        currency: _.isEmpty(currency) ? undefined : currency?.slice(0, 3),
        trading_amount__gte: tradingAmount ?? undefined,
        payment_methods: _.isEmpty(paymentMethods)
          ? undefined
          : paymentMethods?.map((item) => {
              return {
                id: Number(item),
              };
            }),
      }),
      drop: 10,
      page: Number(page) ?? 1,
    });
    return res.data;
  };

  const { data, isLoading, isFetching } = useQuery<OffersData>({
    queryKey: [
      "fetch_offers",
      currency,
      coin,
      offerType,
      debAmount,
      paymentMethods,
    ],
    queryFn: fetchOffers,
  });

  if (isLoading || isFetching) return <Loader />;

  if ((!isLoading || !isFetching) && isEmpty(data?.info))
    return <EmptyPage description="No results found" />;

  return (
    <div className="md:mt-5 divide-y divide-gray-100 dark:divide-grey-500">
      <div className="md:grid hidden  grid-cols-5 p-2 font-semibold bg-white dark:bg-grey-500">
        <span className={textVariants({ variant: "desc" })}>
          {trans("table.headers.advertisers")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("table.headers.rate")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("table.headers.quantity")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("table.headers.paymentMethods")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("table.headers.action")}
        </span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-grey-500 md:grid hidden ">
        {data?.info?.map((offer) => (
          // Desktop view
          <div key={offer?.uuid}>
            <div className="grid grid-cols-5 py-8 px-2">
              <div className="flex gap-2 ">
                <div>
                  <Avatar size={"sm"} color="blue" radius={"xl"}>
                    {offer?.trader__username[0].toUpperCase()}{" "}
                  </Avatar>
                </div>
                <div className="grid">
                  <span
                    onClick={() =>
                      navigate(
                        `/app/trade/trader_info/${offer?.trader__username}`
                      )
                    }
                    className={cn(
                      textVariants({ size: "sm", case: "capitalize" }),
                      "cursor-pointer"
                    )}
                  >
                    {" "}
                    {offer?.trader__username}
                  </span>
                  <span
                    className={textVariants({
                      size: "xs",
                      variant: "desc",
                      font: "semibold",
                    })}
                  >
                    {trans("table.body.transactions", {
                      total: offer?.transactions ?? 0,
                      percentage: offer?.completed,
                    })}
                  </span>
                </div>
              </div>
              <div className="grid">
                <span className={textVariants({ font: "bold", size: "xl" })}>
                  {offer?.currency} {formatNumber(offer?.unit_price)}
                </span>
              </div>
              <span className="flex flex-col gap-2">
                <span className={textVariants({ size: "sm", variant: "desc" })}>
                  {trans("table.body.quantity")}
                  <span className={textVariants({ font: "bold" })}>
                    {" "}
                    {formatNumber(offer?.trading_amount)}{" "}
                    <span className="uppercase">{offer?.currency}</span>
                  </span>
                </span>
                <span className={textVariants({ size: "sm", variant: "desc" })}>
                  {trans("table.body.limits")}
                  <span className={textVariants({ size: "sm" })}>
                    {" "}
                    {formatNumber(offer?.min_order_price)} -{" "}
                    {formatNumber(offer?.max_order_price)}
                  </span>
                </span>
              </span>
              <span className="flex flex-col gap-2">
                {offer?.payment_method.map((item) => (
                  <div className="flex items-center gap-1" key={item?.id}>
                    <img
                      className="h-4 w-4 rounded-full"
                      src={item?.image}
                      alt={item?.name}
                    />
                    <span className={cn(textVariants(), "pl-1")}>
                      {item?.name}
                    </span>{" "}
                  </div>
                ))}
              </span>
              <span>
                <Button
                  // variant={offer?.offer_type === "sell" ? "primary" : "warning"}
                  onClick={() => [setView(true), setSingleOffer(offer)]}
                  className={cn(
                    " px-4 py-2 capitalize font-semibold flex gap-2 items-center",
                    offer?.offer_type === "buy" && "bg-red-400"
                  )}
                >
                  {offer?.offer_type === "sell"
                    ? trans("table.buttons.buy")
                    : trans("table.buttons.sell")}{" "}
                  <img src={offer?.coin__image} alt="" className="h-5 w-5" />{" "}
                </Button>
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="md:hidden grid gap-3 divide-y divide-gray-200 dark:divide-grey-500 pt-4">
        {data?.info.map((offer) => (
          <div
            onClick={() => [setView(true), setSingleOffer(offer)]}
            className="pt-3"
            key={offer?.uuid}
          >
            <div className="grid gap-0.5 ">
              <div className={cn("flex items-center gap-2 mb-2")}>
                <div>
                  <Avatar size={"sm"} color="blue" radius={"xl"}>
                    {offer?.trader__username[0]}{" "}
                  </Avatar>
                </div>
                <div className={cn(textVariants({ font: "medium" }))}>
                  {offer?.trader__username}
                </div>
              </div>
              <span
                className={cn(
                  textVariants({ size: "xs", variant: "desc" }),
                  "-mt-1.5"
                )}
              >
                {trans("table.body.transactions", {
                  total: offer?.transactions ?? 0,
                  percentage: offer?.completed,
                })}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="grid gap-1">
                <div className="flex gap-2 items-center">
                  <span
                    className={cn(
                      textVariants({ font: "semibold", size: "xs" }),
                      "mt-2"
                    )}
                  >
                    {offer?.currency}
                  </span>
                  <span className={textVariants({ size: "xl", font: "bold" })}>
                    {formatNumber(offer?.unit_price)}
                  </span>
                </div>
                <span className={textVariants({ size: "xs", variant: "desc" })}>
                  {trans("table.body.quantity")}
                  <span
                    className={textVariants({ size: "xs", case: "uppercase" })}
                  >
                    {formatNumber(offer?.trading_amount)} {offer?.currency}
                  </span>
                </span>
                <span className={textVariants({ size: "xs", variant: "desc" })}>
                  {trans("table.body.limits")}

                  <span className={textVariants({ size: "xs" })}>
                    {" "}
                    {formatNumber(offer?.min_order_price)} -{" "}
                    {formatNumber(offer?.max_order_price)} {""}
                    {offer?.currency}
                  </span>
                </span>
              </div>
              <div className="flex ">
                <Button
                  onClick={() => [setView(true), setSingleOffer(offer)]}
                  className={cn(
                    " py-1 capitalize font-semibold flex items-center gap-1 ",
                    offer?.offer_type === "buy" && "bg-red-400"
                  )}
                >
                  {offer?.offer_type === "sell"
                    ? trans("table.buttons.buy")
                    : trans("table.buttons.sell")}{" "}
                  <img
                    src={offer?.coin__image}
                    className="h-5 w-5"
                    alt="coin"
                  />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {offer?.payment_method.map((item) => (
                <div className="flex items-center gap-1" key={item?.id}>
                  <img
                    className="h-4 w-4 rounded-full"
                    src={item?.image}
                    alt={item?.name}
                  />
                  <span className={cn(textVariants({ size: "xs" }))}>
                    {item?.name}
                  </span>{" "}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="py-2 mt-3 flex items-center justify-between">
        <span
          className={cn(
            textVariants({ variant: "desc" }),
            "md:text-sm text-xs"
          )}
        >
          {/* Showing {""}
          <span className="font-semibold">{data?.paginator?.items}</span> items
          on <span className="font-semibold">Page {page}</span> */}
          {lang("paginator.desc", {
            items: data?.paginator?.items,
            total: page,
          })}
        </span>
        <div>
          <div className="border border-gray-300 dark:border-grey-500 rounded-lg flex  px-2 w-20 justify-between divide-x divide-gray-300 dark:divide-grey-500">
            <button
              disabled={!data?.paginator?.prev}
              onClick={() => page >= 1 && setPage(Number(page) - 1)}
              className={cn(
                !data?.paginator?.prev
                  ? "text-gray-400 dark:text-grey-400 "
                  : "text-black dark:text-white",
                "py-1 flex-1 w-full flex items-center justify-center cursor-pointer"
              )}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              disabled={!data?.paginator?.next}
              onClick={() => page >= 1 && setPage(Number(page) + 1)}
              className={cn(
                !data?.paginator?.next
                  ? "text-gray-400 dark:text-grey-400"
                  : "text-black dark:text-white",
                "py-1 flex-1 w-full flex items-center justify-center cursor-pointer"
              )}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      <Modal size={"lg"} open={view} setOpen={setView} setter="state" unstyled>
        <PreviewOffer setView={setView} offer={singleOffer} />
      </Modal>
    </div>
  );
};

export default Offers;
